import type { ConstraintDisplayMode } from '@/portfolio/proposal-builder/components/interactive-bar-chart/types'

import { Currency } from '@moment/trading-types/reference-data'
import isNil from 'lodash/isNil'

import {
  getLadderPolicyDisplay,
  getLadderPolicySpan,
} from '@/portfolio/proposal-builder/components/risk-factor-list/helpers/get-ladder-policy-display'
import { getMaturityBasis } from '@/portfolio/proposal-builder/data/transformers/ladder-to-constraints'
import { isDollarMode } from '@/portfolio/shared/display-mode/utils'
import {
  type AllocationConstraint,
  type AverageValueConstraint,
  type DiversificationConstraint,
  type LadderPolicyConstraint,
  type ModelConstraint,
  ModelConstraintType,
  type PiecewiseConstraint,
} from '@/types/api/constraints'
import { describeFilter as describeFilterTree } from '@/utils/live-inventory/describe-filter'
import {
  type LeafConditionStyle,
  SYMBOLIC_STYLE,
  WORDED_STYLE,
} from '@/utils/live-inventory/describe-filter-condition'
import { formatMoney } from '@/utils/presentation/formatting'

import {
  formatBoundMoney,
  formatRuleRange,
  formatRuleWeight,
} from './format-rule-value'
import {
  type AccountValueBound,
  readAccountValueBound,
} from './read-account-value-bound'
import {
  getFieldDisplayName,
  getFieldFormatter,
  MAX_FILTER_DEPTH,
  renderCashflowBandRange,
  renderEqualWeightTolerance,
  selectAllocationBounds,
  selectDiversificationBounds,
} from './utils'

interface RuleText {
  label: string
  value: string | null
}

interface SubRow {
  label: string
  value: string | null // Null for the fallback rung.
}

export type RuleBody =
  | { kind: 'single'; rule: RuleText }
  | { kind: 'grouped'; rule: RuleText; rows: SubRow[] }

interface DescribeParams {
  constraint: ModelConstraint
  displayMode: ConstraintDisplayMode
  displayScaleFactor?: number
  filterListNames: ReadonlyMap<string, string>
  /** ISO code for money bounds; USD when absent. */
  currency?: string
}

const single = (label: string, value: string | null): RuleBody => ({
  kind: 'single',
  rule: { label, value },
})

export function describeConstraint(params: DescribeParams): RuleBody | null {
  const {
    constraint,
    displayMode,
    displayScaleFactor,
    filterListNames,
    currency,
  } = params
  switch (constraint.type) {
    case ModelConstraintType.Universe:
    case ModelConstraintType.BuyUniverse: {
      const label = describeRuleFilter(
        constraint.filter,
        filterListNames,
        currency,
      )
      return label ? single(label, null) : null
    }
    case ModelConstraintType.Allocation:
      return describeAllocation(
        constraint,
        displayMode,
        displayScaleFactor,
        filterListNames,
        currency,
      )
    case ModelConstraintType.Diversification:
      return single(
        ...describeDiversification(
          constraint,
          displayMode,
          displayScaleFactor,
          currency,
        ),
      )
    case ModelConstraintType.AverageValue:
      return single(
        `Average ${getFieldDisplayName(constraint.field)}`,
        describeAverageValueRange(constraint, currency),
      )
    case ModelConstraintType.CashflowBand:
      return single('Level cash flows', renderCashflowBandRange(constraint))
    case ModelConstraintType.LadderPolicy:
      return describeLadderPolicy(constraint, currency)
    case ModelConstraintType.Piecewise:
      return describePiecewise(params, constraint)
    default:
      return null
  }
}

function describeRuleFilter(
  filter: unknown,
  filterListNames: ReadonlyMap<string, string>,
  currency: string = Currency.USD,
  style: LeafConditionStyle = SYMBOLIC_STYLE,
): string {
  return describeFilterTree(filter, {
    style,
    labelField: getFieldDisplayName,
    getValueFormatter: (field) => getFieldFormatter(field, currency),
    resolveListName: (id) => filterListNames.get(id) ?? id,
    maxDepth: MAX_FILTER_DEPTH,
    depthOverflowText: '',
  })
}

function weightFormatter(
  displayMode: ConstraintDisplayMode,
  displayScaleFactor: number | undefined,
  currency: string = Currency.USD,
): (weight: number) => string {
  if (
    displayScaleFactor !== undefined &&
    isDollarMode(displayMode, displayScaleFactor)
  ) {
    return (weight) => formatMoney(weight * displayScaleFactor, { currency })
  }
  return formatRuleWeight
}

const formatOptional = (
  value: number | undefined,
  format: (value: number) => string,
): string | undefined => (value === undefined ? undefined : format(value))

export function describeAllocationRange(
  constraint: AllocationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
  currency?: string,
): string | null {
  const bounds = selectAllocationBounds(constraint)
  const format =
    bounds.kind === 'market_value'
      ? (value: number) => formatBoundMoney(value, currency)
      : weightFormatter(displayMode, displayScaleFactor, currency)
  const min = formatOptional(bounds.min, format)
  const max = formatOptional(bounds.max, format)
  const target = formatOptional(bounds.target, format)
  // An allocation target reads as the bare value; formatRuleRange keeps "= " for other rules.
  if (target && !(min && max)) return target
  return formatRuleRange({ min, max })
}

function describeAllocation(
  constraint: AllocationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor: number | undefined,
  filterListNames: ReadonlyMap<string, string>,
  currency: string | undefined,
): RuleBody {
  const filterText = describeRuleFilter(
    constraint.filter,
    filterListNames,
    currency,
    WORDED_STYLE,
  )
  const label = filterText ? `Allocate to ${filterText}` : 'Allocation'
  const range = describeAllocationRange(
    constraint,
    displayMode,
    displayScaleFactor,
    currency,
  )
  return single(label, range)
}

function describeDiversification(
  constraint: DiversificationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor: number | undefined,
  currency: string | undefined,
): [string, string | null] {
  const field = getFieldDisplayName(constraint.field)
  if (constraint.equal_weight_tolerance != null) {
    return [
      `Equal weight per ${field}`,
      renderEqualWeightTolerance(constraint.equal_weight_tolerance),
    ]
  }

  const bounds = selectDiversificationBounds(constraint)
  let prefix: string
  let format: (value: number) => string
  switch (bounds.kind) {
    case 'instruments':
      prefix = 'Instruments per'
      format = String
      break
    case 'market_value':
      prefix = 'Market value per'
      format = (value) => formatBoundMoney(value, currency)
      break
    case 'weight':
      prefix = isDollarMode(displayMode, displayScaleFactor)
        ? 'Market value per'
        : 'Weight per'
      format = weightFormatter(displayMode, displayScaleFactor, currency)
      break
    default:
      return bounds satisfies never
  }
  const range = formatRuleRange({
    min: formatOptional(bounds.min, format),
    max: formatOptional(bounds.max, format),
  })
  if (range) return [`${prefix} ${field}`, range]

  const countRange = formatRuleRange({
    min: formatOptional(constraint.min_unique_fields, String),
    max: formatOptional(constraint.max_unique_fields, String),
  })
  if (countRange) return [`Unique ${field} count`, countRange]
  return [`${prefix} ${field}`, null]
}

function describeAverageValueRange(
  constraint: AverageValueConstraint,
  currency: string = Currency.USD,
): string | null {
  const format = getFieldFormatter(constraint.field, currency)
  const read = (value: number | string | null | undefined) =>
    isNil(value) ? undefined : format(value)
  const min = read(constraint.min_average_value)
  const max = read(constraint.max_average_value)
  // The sentence writer prefers a bound over the target; keep that order.
  if (min || max) return formatRuleRange({ min, max })
  return formatRuleRange({ target: read(constraint.target_average_value) })
}

export function describeLadderPolicy(
  constraint: LadderPolicyConstraint,
  currency?: string,
): RuleBody {
  const { rungs } = getLadderPolicyDisplay(constraint, currency)
  return {
    kind: 'grouped',
    rule: {
      label: `Ladder by ${getMaturityBasis(constraint).label}`,
      value: getLadderPolicySpan(constraint),
    },
    rows: rungs.map((rung) => ({ label: rung.range, value: rung.value })),
  }
}

// Non-last tiers carry only an upper bound, so a tier's lower edge is the
// previous tier's upper bound.
function describeTierBand(
  { upper, lower: explicitLower }: AccountValueBound,
  previousUpper: number | undefined,
  currency: string | undefined,
): string {
  const lower = explicitLower ?? previousUpper
  const money = (value: number) => formatBoundMoney(value, currency)
  if (upper !== undefined && lower !== undefined) {
    return `${money(lower)} – ${money(upper)}`
  }
  if (upper !== undefined) return `Up to ${money(upper)}`
  if (lower !== undefined) return `Above ${money(lower)}`
  return 'All accounts'
}

function describePiecewise(
  params: DescribeParams,
  constraint: PiecewiseConstraint,
): RuleBody | null {
  const sortKey = (bound: AccountValueBound) =>
    bound.upper ?? Number.POSITIVE_INFINITY
  const pieces = constraint.pieces
    .map((piece) => ({ piece, bound: readAccountValueBound(piece.condition) }))
    .sort((a, b) => sortKey(a.bound) - sortKey(b.bound) || 0)
  const tiers: { band: string; rule: RuleText }[] = []
  let previousUpper: number | undefined
  for (const { piece, bound } of pieces) {
    const band = describeTierBand(bound, previousUpper, params.currency)
    previousUpper = bound.upper ?? previousUpper
    const [child, ...rest] = piece.constraints
    if (!child || rest.length > 0) continue
    const body = describeConstraint({ ...params, constraint: child })
    if (body) tiers.push({ band, rule: body.rule })
  }
  const [first] = tiers
  if (!first) return null

  const sameLabel = tiers.every((tier) => tier.rule.label === first.rule.label)
  if (sameLabel) {
    return {
      kind: 'grouped',
      rule: { label: first.rule.label, value: null },
      rows: tiers.map(({ band, rule }) => ({ label: band, value: rule.value })),
    }
  }
  return {
    kind: 'grouped',
    rule: { label: 'Tiered by account size', value: null },
    rows: tiers.map(({ band, rule }) => ({
      label: band,
      value: rule.value ? `${rule.label} ${rule.value}` : rule.label,
    })),
  }
}
